/// <reference types="vite/client" />
import { Student, EducationConsultant, Teacher, ExamLibraryItem, YearlyRoadmap, User, Parent, GradeTopicConfig } from '../types';
import { hataMesaji, oturumBittiMi } from '../utils/hataMesaji';

// Dynamic API URL: 
// - Production (Netlify/Vercel): Use same origin (API is served via serverless functions)
// - Development: Use localhost:3004
export const getApiUrl = () => {
    if (typeof window !== 'undefined') {
        const host = window.location.hostname;
        const protocol = window.location.protocol;

        // 1. Localhost / Local IP development - always target backend port 3004
        if (host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.')) {
            return `http://${host}:3004`;
        }

        // 2. Production domains - same-origin
        if (protocol === 'https:' || host.includes('supervisoregitim.com') || host.includes('netlify.app') || host.includes('vercel.app')) {
            console.log("API URL resolved to: (same origin)");
            return '';
        }
    }

    // Fallback to explicit environment variable
    const envUrl = import.meta.env.VITE_API_URL;
    if (envUrl && !envUrl.includes('supervisoregitim.com')) {
        return envUrl;
    }

    return 'http://localhost:3004';
};

export const API_URL = getApiUrl();

/**
 * Giriş sırasında saklanan JWT'yi Authorization başlığı olarak döner.
 *
 * Token `login` içinde localStorage'a yazılıyordu ama hiçbir istekte
 * gönderilmiyordu; bu yüzden sunucu tarafında korumalı olan uçlar tarayıcıdan
 * çağrılamıyordu. Korunması gereken her istek bu başlığı eklemelidir.
 *
 * Token yoksa boş nesne döner, böylece korumasız uçlar etkilenmez.
 */
export const authHeaders = (): Record<string, string> => {
    if (typeof window === 'undefined') return {};
    const token = window.localStorage.getItem('auth_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Sunucu, bu sürümden önce üretilmiş (scope iddiası taşımayan) token'ları
 * TOKEN_OUTDATED ile reddediyor. O token'la gezinen kullanıcı, sebebini
 * anlamadan çalışmayan bir panel görmesin diye oturum burada temizlenip
 * giriş ekranına düşülür.
 */
const oturumuSonlandir = () => {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem('auth_token');
    window.localStorage.removeItem('supervisor_user');
    window.location.reload();
};

/**
 * Authorization başlığını otomatik ekleyen fetch sarmalayıcısı.
 *
 * Bu dosyadaki tüm istekler bunun üzerinden geçer; böylece yeni bir uç
 * eklenirken token göndermeyi unutmak mümkün olmaz. Çağrının kendi
 * başlıkları (ör. Content-Type) korunur, üzerine Authorization eklenir.
 *
 * Token yoksa authHeaders() boş nesne döndüğü için istek eskisi gibi,
 * başlıksız gider — korumasız uçların davranışı değişmez.
 */
export const apiFetch = async (input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> => {
    const response = await globalThis.fetch(input, {
        ...init,
        headers: { ...(init.headers as Record<string, string> | undefined), ...authHeaders() },
    });

    // Yanıt gövdesi çağırana bozulmadan gitmeli; kontrol kopya üzerinden yapılır.
    if (response.status === 401 && typeof window !== 'undefined' && window.localStorage.getItem('auth_token')) {
        try {
            const govde = await response.clone().json();
            if (oturumBittiMi(govde)) oturumuSonlandir();
        } catch {
            // Gövde JSON değilse yapacak bir şey yok, yanıt olduğu gibi döner.
        }
    }

    return response;
};

export const api = {
    getStudents: async (): Promise<Student[]> => {
        const response = await apiFetch(`${API_URL}/students`);
        if (!response.ok) throw new Error('Failed to fetch students');
        const data = await response.json();
        return data.map((s: any) => ({ ...s, id: s.id }));
    },

    getStudent: async (id: number | string): Promise<Student> => {
        const response = await apiFetch(`${API_URL}/students/${id.toString()}`);
        if (!response.ok) throw new Error('Failed to fetch student');
        const data = await response.json();
        return { ...data, id: data.id };
    },

    updateStudent: async (student: Student): Promise<Student> => {
        const response = await apiFetch(`${API_URL}/students/${student.id.toString()}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(student),
        });
        if (!response.ok) throw new Error('Failed to update student');
        const data = await response.json();
        return { ...data, id: data.id };
    },

    createStudent: async (student: Partial<Student>): Promise<Student> => {
        const response = await apiFetch(`${API_URL}/students`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(student),
        });
        if (!response.ok) throw new Error('Failed to create student');
        return response.json();
    },

    getConsultants: async (): Promise<EducationConsultant[]> => {
        const response = await apiFetch(`${API_URL}/consultants`);
        if (!response.ok) throw new Error('Failed to fetch consultants');
        const data = await response.json();
        return data.map((c: any) => ({
            ...c,
            id: c.id,
            studentIds: c.studentIds || []
        }));
    },

    getConsultantById: async (id: string | number): Promise<EducationConsultant> => {
        const response = await apiFetch(`${API_URL}/consultants/${id}`);
        if (!response.ok) throw new Error('Failed to fetch consultant');
        const data = await response.json();
        return {
            ...data,
            id: data.id,
            studentIds: data.studentIds || []
        };
    },

    updateConsultant: async (consultant: EducationConsultant): Promise<EducationConsultant> => {
        const response = await apiFetch(`${API_URL}/consultants/${consultant.id.toString()}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...consultant, id: consultant.id.toString() }),
        });
        if (!response.ok) throw new Error('Failed to update consultant');
        return response.json();
    },

    createConsultant: async (consultant: Partial<EducationConsultant>): Promise<EducationConsultant> => {
        const response = await apiFetch(`${API_URL}/consultants`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(consultant),
        });
        if (!response.ok) throw new Error('Failed to create consultant');
        const data = await response.json();
        return { ...data, id: data.id };
    },

    getTeachers: async (): Promise<Teacher[]> => {
        const response = await apiFetch(`${API_URL}/teachers`);
        if (!response.ok) throw new Error('Failed to fetch teachers');
        const data = await response.json();
        return data.map((t: any) => ({ ...t, id: t.id }));
    },

    getTeacherById: async (id: string | number): Promise<Teacher> => {
        const response = await apiFetch(`${API_URL}/teachers/${id}`);
        if (!response.ok) throw new Error('Failed to fetch teacher');
        const data = await response.json();
        return { ...data, id: data.id };
    },

    updateTeacher: async (teacher: Teacher): Promise<Teacher> => {
        const response = await apiFetch(`${API_URL}/teachers/${teacher.id.toString()}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(teacher),
        });
        if (!response.ok) throw new Error('Failed to update teacher');
        const data = await response.json();
        return { ...data, id: data.id };
    },

    createTeacher: async (teacher: Partial<Teacher>): Promise<Teacher> => {
        const response = await apiFetch(`${API_URL}/teachers`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(teacher),
        });
        if (!response.ok) throw new Error('Failed to create teacher');
        const data = await response.json();
        return { ...data, id: data.id };
    },
    getRanking: async (studentId: string | number, type: string): Promise<{ rank: number, total: number, avgNet: string }> => {
        const response = await apiFetch(`${API_URL}/students/${studentId.toString()}/ranking?type=${type}`);
        if (!response.ok) throw new Error('Failed to fetch ranking');
        return response.json();
    },


    getExamLibrary: async (): Promise<ExamLibraryItem[]> => {
        const response = await apiFetch(`${API_URL}/examLibrary`);
        if (!response.ok) throw new Error('Failed to fetch exam library');
        return response.json();
    },

    createExam: async (exam: ExamLibraryItem): Promise<ExamLibraryItem> => {
        const response = await apiFetch(`${API_URL}/examLibrary`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(exam),
        });
        if (!response.ok) throw new Error('Failed to create exam');
        return response.json();
    },

    getYearlyRoadmap: async (): Promise<YearlyRoadmap[]> => {
        const response = await apiFetch(`${API_URL}/yearlyRoadmap`);
        if (!response.ok) throw new Error('Failed to fetch yearly roadmap');
        return response.json();
    },

    login: async (email: string, pass: string, isEduAnaliz?: boolean, selectedRole?: string, institutionCode?: string, turnstileToken?: string): Promise<User | null> => {
        try {
            const response = await apiFetch(`${API_URL}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password: pass, isEduAnaliz, selectedRole, institutionCode, turnstileToken }),
            });

            if (response.ok) {
                const user = await response.json();
                if (user && user.token) {
                    localStorage.setItem('auth_token', user.token);
                }
                return user;
            }
            const errData = await response.json().catch(() => ({}));
            if (errData && errData.error) {
                // Sunucunun `code` alanı (ör. EMAIL_NOT_VERIFIED) hataya
                // iliştiriliyor; arayüz buna göre farklı bir ekran gösteriyor.
                const err: any = new Error(errData.error);
                if (errData.code) err.code = errData.code;
                if (errData.email) err.email = errData.email;
                throw err;
            }
            return null;
        } catch (error: any) {
            console.error("Login failed:", error);
            throw error;
        }
    },

    getParents: async (): Promise<Parent[]> => {
        const response = await apiFetch(`${API_URL}/parents`);
        if (!response.ok) throw new Error('Failed to fetch parents');
        const data = await response.json();
        return data.map((p: any) => ({
            ...p,
            id: p.id,
            studentIds: p.studentIds || []
        }));
    },

    updateParent: async (parent: Parent): Promise<Parent> => {
        const response = await apiFetch(`${API_URL}/parents/${parent.id.toString()}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...parent, id: parent.id.toString() }),
        });
        if (!response.ok) throw new Error('Failed to update parent');
        return response.json();
    },

    deleteStudent: async (id: number | string): Promise<void> => {
        // Sunucuda requireAdmin ile korunuyor; token apiFetch tarafından eklenir.
        const response = await apiFetch(`${API_URL}/students/${id.toString()}`, {
            method: 'DELETE',
        });
        if (!response.ok) throw new Error('Failed to delete student');
    },

    deleteTeacher: async (id: number | string): Promise<void> => {
        const response = await apiFetch(`${API_URL}/teachers/${id.toString()}`, {
            method: 'DELETE',
        });
        if (!response.ok) throw new Error('Failed to delete teacher');
    },

    deleteConsultant: async (id: number | string): Promise<void> => {
        const response = await apiFetch(`${API_URL}/consultants/${id.toString()}`, {
            method: 'DELETE',
        });
        if (!response.ok) throw new Error('Failed to delete consultant');
    },


    logSystemError: async (error: {
        type: 'error' | 'warning' | 'info',
        source: string,
        message: string,
        details?: string,
        userId?: number | string,
        userName?: string
    }): Promise<void> => {
        try {
            await apiFetch(`${API_URL}/api/system-errors`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(error),
            });
        } catch (err) {
            console.error('Failed to log system error:', err);
        }
    },

    // Curriculums
    getCurriculums: async (params?: { grade?: string; department?: string; weekNumber?: number; studentId?: string | number; targetType?: string; targetId?: string | null }): Promise<any[]> => {
        const query = new URLSearchParams(params as any).toString();
        const response = await apiFetch(`${API_URL}/api/curriculums${query ? `?${query}` : ''}`);
        if (!response.ok) throw new Error('Failed to fetch curriculums');
        return response.json();
    },

    saveCurriculum: async (curriculum: any): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/curriculums`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(curriculum),
        });
        if (!response.ok) throw new Error('Failed to save curriculum');
        return response.json();
    },

    deleteCurriculum: async (id: string): Promise<void> => {
        const response = await apiFetch(`${API_URL}/api/curriculums/${id}`, {
            method: 'DELETE',
        });
        if (!response.ok) throw new Error('Failed to delete curriculum');
    },

    getGradeTopics: async (grade: string): Promise<GradeTopicConfig[]> => {
        const response = await apiFetch(`${API_URL}/api/grade-topics?grade=${encodeURIComponent(grade)}`);
        if (!response.ok) throw new Error('Failed to fetch grade topics');
        return response.json();
    },

    /**
     * Konu Yönetimi'nde tanımlı ders adları, kapsam kırılımında.
     * Ders Eşleştirme ekranı kanonik adı buradan öneriyor — kanonik ad
     * müfredattaki adla aynı olsun ki planlama ekranları iki listeyi
     * ad benzerliğiyle buluşturmak zorunda kalmasın.
     */
    getGradeTopicSubjects: async (): Promise<{ ad: string; kapsamlar: string[] }[]> => {
        const response = await apiFetch(`${API_URL}/api/grade-topics/dersler`);
        if (!response.ok) throw new Error('Konu Yönetimi ders listesi alınamadı.');
        const data = await response.json();
        return data.dersler || [];
    },

    getMistakenQuestions: async (studentId: string): Promise<any[]> => {
        const response = await apiFetch(`${API_URL}/api/topic-quizzes/mistaken-questions/${studentId}`);
        if (!response.ok) throw new Error('Failed to fetch mistaken questions');
        return response.json();
    },
    getTopicQuiz: async (quizId: string): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/topic-quizzes/${quizId}`);
        if (!response.ok) throw new Error('Failed to fetch topic quiz');
        return response.json();
    },
    submitQuizResult: async (quizId: string, result: {
        studentId: string;
        answers: number[];
        timeSpentSeconds: number;
        questionTimes?: number[];
        teacherId?: string;
        classId?: string;
    }): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/topic-quizzes/${quizId}/submit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(result),
        });
        if (!response.ok) throw new Error('Failed to submit quiz result');
        return response.json();
    },

    getTeacherFrequencyAnalysis: async (quizId: string, classId: string): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/topic-quizzes/teacher/frequency-analysis/${quizId}/${classId}`);
        if (!response.ok) throw new Error('Failed to fetch frequency analysis');
        return response.json();
    },

    getTeacherClassResults: async (quizId: string, classId: string): Promise<any[]> => {
        const response = await apiFetch(`${API_URL}/api/topic-quizzes/teacher/class-results/${quizId}/${classId}`);
        if (!response.ok) throw new Error('Failed to fetch class results');
        return response.json();
    },

    getTeacherQuizzes: async (teacherId: string | number): Promise<any[]> => {
        const response = await apiFetch(`${API_URL}/api/topic-quizzes/teacher/quizzes/${teacherId.toString()}`);
        if (!response.ok) throw new Error('Failed to fetch teacher quizzes');
        return response.json();
    },

    getAssignedQuizzes: async (teacherId: string | number, classId: string): Promise<any[]> => {
        const response = await apiFetch(`${API_URL}/api/topic-quizzes/teacher/assigned/${teacherId.toString()}/${classId}`);
        if (!response.ok) throw new Error('Failed to fetch assigned quizzes');
        return response.json();
    },

    getQuizzes: async (params?: { subject?: string; topic?: string; examType?: string; teacherId?: string | number }): Promise<any[]> => {
        const query = new URLSearchParams(params as any).toString();
        const response = await apiFetch(`${API_URL}/api/topic-quizzes${query ? `?${query}` : ''}`);
        if (!response.ok) throw new Error('Failed to fetch quizzes');
        return response.json();
    },

    assignQuiz: async (data: {
        quizId: string;
        teacherId: string | number;
        classId?: string;
        studentIds?: string[];
        subject: string;
        topic: string;
        examType: string;
    }): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/topic-quizzes/assign`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Failed to assign quiz');
        return response.json();
    },

    // Class Management
    getClasses: async (teacherId: string | number): Promise<any[]> => {
        const response = await apiFetch(`${API_URL}/api/classes?teacherId=${teacherId.toString()}`);
        if (!response.ok) throw new Error('Failed to fetch classes');
        return response.json();
    },

    createClass: async (data: any): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/classes`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Failed to create class');
        return response.json();
    },

    updateClass: async (id: string, data: any): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/classes/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        if (!response.ok) throw new Error('Failed to update class');
        return response.json();
    },

    deleteClass: async (id: string): Promise<void> => {
        const response = await apiFetch(`${API_URL}/api/classes/${id}`, {
            method: 'DELETE',
        });
        if (!response.ok) throw new Error('Failed to delete class');
    },

    searchStudents: async (query: string): Promise<any[]> => {
        const response = await apiFetch(`${API_URL}/api/students/search?query=${encodeURIComponent(query)}`);
        if (!response.ok) throw new Error('Failed to search students');
        return response.json();
    },

    // EduAnaliz API
    getEduAnalizTests: async (createdBy?: string): Promise<any[]> => {
        const url = createdBy ? `${API_URL}/api/eduanaliz/tests?createdBy=${createdBy}` : `${API_URL}/api/eduanaliz/tests`;
        const response = await apiFetch(url);
        if (!response.ok) throw new Error('Failed to fetch EduAnaliz tests');
        return response.json();
    },
    saveEduAnalizTest: async (test: any): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/tests`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(test),
        });
        if (!response.ok) throw new Error('Failed to save EduAnaliz test');
        return response.json();
    },
    deleteEduAnalizTest: async (id: string): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/tests/${id}`, { method: 'DELETE' });
        if (!res.ok) throw new Error('Failed to delete test');
        return res.json();
    },

    getEduAnalizSurveys: async (createdBy?: string): Promise<any[]> => {
        const url = createdBy ? `${API_URL}/api/eduanaliz/surveys?createdBy=${createdBy}` : `${API_URL}/api/eduanaliz/surveys`;
        const response = await apiFetch(url);
        if (!response.ok) throw new Error('Failed to fetch EduAnaliz surveys');
        return response.json();
    },
    saveEduAnalizSurvey: async (survey: any): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/surveys`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(survey),
        });
        if (!response.ok) throw new Error('Failed to save EduAnaliz survey');
        return response.json();
    },
    /**
     * Ankete tek yanıt ekler (öğrenci paneli, anket linki). Anketin tamamını
     * POST /surveys'e göndermeyin: `responses` dizisini değiştirir ve başka
     * öğrencilerin yanıtlarını silebilir. Dönen `yanit` sunucunun kaydettiğidir.
     */
    anketYanitiGonder: async (id: string, govde: { name?: string; className?: string; answers: any }): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/surveys/${encodeURIComponent(id)}/yanit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(govde),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(hataMesaji(data, 'Yanıt gönderilemedi. İnternet bağlantınızı kontrol edip tekrar deneyin.'));
        return data.yanit;
    },
    /**
     * Koçun seçtiği yanıtları siler. Anketin tamamını geri yazmayın: koçun
     * listesi eskiyse o arada gelen yanıtlar da silinir.
     */
    anketYanitlariniSil: async (id: string, yanitlar: Array<{ submittedAt?: number; name?: string; studentId?: string }>): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/surveys/${encodeURIComponent(id)}/yanit-sil`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ yanitlar }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(hataMesaji(data, 'Yanıt silinemedi.'));
        return data;
    },
    /** Yaşam Pencerem okul raporunun değerlendirme metni — yalnızca bu alanı yazar. */
    saveYasamDegerlendirme: async (id: string, metin: string): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/surveys/${encodeURIComponent(id)}/degerlendirme`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ metin }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(hataMesaji(data, 'Değerlendirme kaydedilemedi.'));
        return data;
    },
    deleteEduAnalizSurvey: async (id: string): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/surveys/${id}`, { method: 'DELETE' });
        if (!res.ok) throw new Error('Failed to delete survey');
        return res.json();
    },

    // ── Rutin (tekrarlayan) ödevler ──
    getEduAnalizRoutines: async (studentId?: string): Promise<any[]> => {
        const url = studentId
            ? `${API_URL}/api/eduanaliz/routines?studentId=${encodeURIComponent(studentId)}`
            : `${API_URL}/api/eduanaliz/routines`;
        const response = await apiFetch(url);
        if (!response.ok) return [];
        return response.json();
    },
    saveEduAnalizRoutine: async (routine: any): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/routines`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(routine),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(hataMesaji(data, 'Rutin kaydedilemedi.'));
        return data;
    },
    /** Rutini durdurur; verilen haftadan itibaren üretilmiş görevleri kaldırır. */
    stopEduAnalizRoutine: async (routineId: string, fromWeek: string): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/routines/${encodeURIComponent(routineId)}/stop`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ fromWeek }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(hataMesaji(data, 'Rutin durdurulamadı.'));
        return data;
    },
    /**
     * Seçili haftanın rutin görevlerini hesaplar (idempotent).
     *
     * Varsayılan ÖNİZLEMEDİR: sonuç kaydedilmez. Koç ekranı hafta oklarıyla
     * ileri gidince her hafta kaydedilir olsaydı veritabanına gelecek
     * haftalar birikir, öğrenci ekranı da onlardan birini "bu hafta" sanırdı.
     */
    materializeEduAnalizWeek: async (studentId: string, weekStart: string, kaydet = false): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/routines/materialize`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ studentId, weekStart, kaydet }),
        });
        if (!response.ok) return null;
        return response.json();
    },

    /**
     * Öğrenciye giriş hesabı açar veya şifresini yeniler.
     * Koç kendi kurumundaki öğrenci için çağırabilir; kurum kodu sunucuda
     * token'dan alınır, istemci gönderemez.
     */
    createEduAnalizStudentAccount: async (studentId: string, email: string, password: string, name?: string): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/students/${encodeURIComponent(studentId)}/account`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, name }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(hataMesaji(data, 'Öğrenci hesabı oluşturulamadı.'));
        return data;
    },

    /**
     * Veliye giriş hesabı açar veya şifresini yeniler ve öğrenci kaydının
     * `parentEmail` alanını aynı çağrıda yazar. Öğrenci hesabıyla aynı uç
     * kalıbı; rol sunucuda sabit, istemci gönderemez.
     */
    createEduAnalizParentAccount: async (studentId: string, email: string, password: string, name?: string): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/students/${encodeURIComponent(studentId)}/veli-hesabi`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, name }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(hataMesaji(data, 'Veli hesabı oluşturulamadı.'));
        return data;
    },

    /**
     * Kurumdaki öğrencilerin sınıf dağılımı — anket editöründeki hedefleme
     * önizlemesi. Sınıfı KAYITLI OLMAYAN öğrenci hedefli bir anketi hiç
     * görmüyor; `sinifsiz` sayısı koça bunu önceden söylüyor.
     */
    getEduAnalizOgrenciSiniflari: async (): Promise<{
        siniflar: { ad: string; sayi: number }[]; sinifsiz: number; toplam: number;
    }> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/ogrenci-siniflari`);
        if (!response.ok) throw new Error('Sınıf dağılımı okunamadı.');
        return response.json();
    },

    /**
     * Veli panelinin TEK veri ucu. Salt okunur; ne döndüğü sunucuda tek tek
     * seçiliyor (server/utils/veliErisim.js), kayıt olduğu gibi gelmiyor.
     * `kod: 'VELI_BAGLI_DEGIL'` → hesap hiçbir öğrenciye bağlı değil.
     */
    getVeliOgrencilerim: async (): Promise<{ ogrenciler: any[]; kod?: string }> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/veli/ogrencilerim`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(hataMesaji(data, 'Veli verileri okunamadı.'));
        return data;
    },

    /**
     * Koçun öğrencilerinde `sonrasi`dan beri ne oldu? Bildirim zilini bu
     * besliyor — socket.io Netlify'da bağlanamadığı için yoklama kullanılıyor
     * (aynı sebeple online sınav paneli de yokluyor).
     */
    getEduAnalizEtkinlik: async (sonrasi?: string): Promise<{ olaylar: any[]; simdi: string }> => {
        const q = sonrasi ? `?sonrasi=${encodeURIComponent(sonrasi)}` : '';
        const response = await apiFetch(`${API_URL}/api/eduanaliz/etkinlik${q}`);
        if (!response.ok) throw new Error('Etkinlik okunamadı.');
        return response.json();
    },

    /**
     * Giriş yapan kullanıcının EduAnaliz profili (ad, kurum adı, fotoğraf).
     * Oturum kopyası girişte alınmış bir anlık görüntü; doğrunun kaynağı bu.
     */
    getEduAnalizProfile: async (): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/profile`);
        if (!response.ok) return null;
        return response.json();
    },

    /** Öğrencinin kendi kaydı — giriş hesabına e-posta ile bağlanır. */
    getEduAnalizMyStudentRecord: async (): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/students/me`);
        if (response.status === 404) return null; // hesap bir öğrenci kaydına bağlı değil
        if (!response.ok) throw new Error('Failed to fetch student record');
        return response.json();
    },
    /** Öğrencinin kendi ödevleri (createdBy değil, studentId ile filtrelenir). */
    getEduAnalizMyHomeworks: async (): Promise<any[]> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/homeworks/mine`);
        if (response.status === 404) return [];
        if (!response.ok) throw new Error('Failed to fetch own homeworks');
        return response.json();
    },
    /**
     * Tek görev işaretleme — kural sunucuda uygulanır.
     *
     * Üç durum: 'yapildi' | 'yapilmadi' | 'bos'. `sebep` yalnızca
     * 'yapilmadi' için anlamlı ve isteğe bağlı ('zaman' | 'anlamadim' |
     * 'kaynak' | 'diger'); sunucu sabit listeye göre süzüyor.
     */
    /** "Geçen hafta yapılmayanlar" kartını kalıcı olarak kapatır (yalnızca koç). */
    kapatDevirHatirlatmasi: async (homeworkId: string, taskId: string): Promise<any> => {
        const response = await apiFetch(
            `${API_URL}/api/eduanaliz/homeworks/${encodeURIComponent(homeworkId)}/tasks/${encodeURIComponent(taskId)}/devir-kapat`,
            { method: 'POST' }
        );
        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(hataMesaji(data, 'Hatırlatma kapatılamadı.'));
        }
        return response.json();
    },

    completeEduAnalizTask: async (
        homeworkId: string, taskId: string,
        durum: 'yapildi' | 'yapilmadi' | 'bos', sebep?: string | null
    ): Promise<any> => {
        const response = await apiFetch(
            `${API_URL}/api/eduanaliz/homeworks/${encodeURIComponent(homeworkId)}/tasks/${encodeURIComponent(taskId)}/complete`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ durum, sebep: sebep || undefined }),
            }
        );
        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(hataMesaji(data, 'Görev işaretlenemedi.'));
        }
        return response.json();
    },

    /** Koç: öğrencinin konu hakimiyetleri */
    getKonuHakimiyeti: async (studentId: string): Promise<any[]> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/students/${encodeURIComponent(studentId)}/konu-hakimiyet`);
        if (!response.ok) return [];
        return response.json();
    },

    /** Öğrenci: kendi konu hakimiyetleri */
    /**
     * Öğrenci kendi konu bitirme işaretini değiştirir.
     *
     * Koçun kullandığı `saveEduAnalizStudent` bütün kaydı yazıyor; öğrenciye
     * o uç açılamaz (ad, PIN, kurum kodu da yazılabilirdi). Bu uç yalnızca
     * `topics` ve `topicTracking` alanlarına, yalnızca kendi kaydına dokunur.
     * Değer açıkça verilir, tersine çevrilmez: yinelenen istek işareti geri
     * almasın.
     */
    setKonuDurumuMine: async (
        gonderi: { ders: string; kod: string; legacyKod?: string | null; kaynakId?: string | null; bitti: boolean }
    ): Promise<{ topics: any; topicTracking: any }> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/students/me/konu-durumu`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(gonderi),
        });
        if (!response.ok) {
            const hata = await response.json().catch(() => ({}));
            throw new Error(hata.message || 'Konu işareti kaydedilemedi.');
        }
        return response.json();
    },

    /** Öğrenci kendi kaynağını ekler/düzenler. Koçunkine dokunamaz (sunucu 403). */
    saveKaynakMine: async (kaynak: any): Promise<{ resources: any[] }> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/students/me/kaynak`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(kaynak),
        });
        if (!response.ok) {
            const hata = await response.json().catch(() => ({}));
            throw new Error(hata.message || 'Kaynak kaydedilemedi.');
        }
        return response.json();
    },

    /**
     * Kaynağın konu başına soru sayısı ("kitapta kaç soru"). Koç ve öğrenci
     * aynı ucu kullanır; 0 sayıyı siler. Dönen liste öğrencinin bütün sayıları.
     */
    saveKaynakSoruSayilari: async (studentId: string, kaynakId: string,
                                   sayilar: Array<{ kod: string; soru: number }>): Promise<{ kaynakSoruSayilari: any[] }> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/students/${encodeURIComponent(studentId)}/kaynak-soru`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ kaynakId, sayilar }),
        });
        if (!response.ok) {
            const hata = await response.json().catch(() => ({}));
            throw new Error(hata.message || 'Soru sayısı kaydedilemedi.');
        }
        return response.json();
    },

    deleteKaynakMine: async (id: string): Promise<{ resources: any[] }> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/students/me/kaynak/${encodeURIComponent(id)}`, {
            method: 'DELETE',
        });
        if (!response.ok) {
            const hata = await response.json().catch(() => ({}));
            throw new Error(hata.message || 'Kaynak silinemedi.');
        }
        return response.json();
    },

    getKonuHakimiyetiMine: async (): Promise<any[]> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/konu-hakimiyet/mine`);
        if (!response.ok) return [];
        return response.json();
    },

    /** Görev sonucu girişi — konu hakimiyetini günceller. */
    // `hazir` verilirse sorunun metnini sunucu kurar (HAZIR_SORULAR);
    // düğme etiketi ile modele giden soru tek kaynaktan gelsin diye.
    sorOgrenciHakkinda: async (
        studentId: string, soru: string, hazir?: string,
    ): Promise<{ cevap: string; olgular: any; raporId: string | null; soru: string }> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/ai/sor`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(hazir ? { studentId, hazir } : { studentId, soru }),
        });
        if (!response.ok) {
            const d = await response.json().catch(() => ({}));
            throw new Error((d as any).message || 'Soru yanıtlanamadı.');
        }
        return response.json();
    },

    // ── Haftalık plan önerisi (kural motoru, AI yok) ────────────────────
    // Yalnızca ÖNERİ döner; kaydetmez. Koç düzenleyip Ödev Atama'dan kaydeder.
    planOner: async (
        studentId: string,
        dersYuku: Record<string, number>,
        denemeGunu?: string | null,
    ): Promise<{ gorevler: any[]; yerlesmeyen: any[]; ozet: any; konuVerisiVar: boolean }> => {
        const response = await apiFetch(
            `${API_URL}/api/eduanaliz/students/${encodeURIComponent(studentId)}/plan-oner`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ dersYuku, denemeGunu: denemeGunu || null }),
            });
        if (!response.ok) {
            const d = await response.json().catch(() => ({}));
            throw new Error((d as any).message || 'Plan önerisi alınamadı.');
        }
        return response.json();
    },

    // ── Rapor arşivi ────────────────────────────────────────────────────
    getRaporlar: async (studentId: string): Promise<any[]> => {
        const response = await apiFetch(
            `${API_URL}/api/eduanaliz/ai/raporlar/${encodeURIComponent(studentId)}`);
        if (!response.ok) return [];
        return response.json();
    },

    deleteRapor: async (id: string): Promise<void> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/ai/raporlar/${encodeURIComponent(id)}`,
            { method: 'DELETE' });
        if (!response.ok) {
            const d = await response.json().catch(() => ({}));
            throw new Error((d as any).message || 'Rapor silinemedi.');
        }
    },

    getKomutaMerkezi: async (): Promise<{ ogrenciler: any[]; ozet: { kritik: number; dikkat: number; normal: number } }> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/komuta`);
        if (!response.ok) throw new Error('Komuta merkezi yüklenemedi.');
        return response.json();
    },

    submitEduAnalizTaskSonuc: async (
        homeworkId: string,
        taskId: string,
        sonuc: { cozulen: number; dogru: number; yanlis: number; bos: number; sureDk: number }
    ): Promise<any> => {
        const response = await apiFetch(
            `${API_URL}/api/eduanaliz/homeworks/${encodeURIComponent(homeworkId)}/tasks/${encodeURIComponent(taskId)}/sonuc`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(sonuc),
            }
        );
        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(hataMesaji(data, 'Sonuç kaydedilemedi.'));
        }
        return response.json();
    },

    // ── Arama (#16) ─────────────────────────────────────────────────────
    aramaYap: async (filtre: Record<string, any>): Promise<any[]> => {
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(filtre)) {
            if (v !== '' && v !== -1 && v !== undefined && v !== null) params.set(k, String(v));
        }
        const response = await apiFetch(`${API_URL}/api/eduanaliz/arama?${params.toString()}`);
        if (!response.ok) throw new Error('Arama yapılamadı.');
        return response.json();
    },

    aramaFiltreCevir: async (sorgu: string): Promise<{ filtre: Record<string, any>; yorumlanmisMetin: string }> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/ai/arama-filtre`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sorgu }),
        });
        if (!response.ok) {
            const d = await response.json().catch(() => ({}));
            throw new Error((d as any).message || 'Sorgu anlaşılamadı.');
        }
        return response.json();
    },

    // ── Online sınav oturumları ─────────────────────────────────────────
    // Oturum sunucuda tutulur; öğretmenin tarayıcısının açık olması gerekmez.

    /** Öğretmen: yeni oturum başlatır, sınav kodunu döndürür. */
    startEduAnalizExamSession: async (payload: { testId: string; classId?: string; className?: string; duration: number }): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/exam-sessions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(hataMesaji(data, 'Sınav oturumu başlatılamadı.'));
        }
        return response.json();
    },
    /** Öğretmen: kendi oturumları — canlı panel bunu periyodik çeker. */
    getEduAnalizExamSessions: async (activeOnly = false): Promise<any[]> => {
        const url = `${API_URL}/api/eduanaliz/exam-sessions${activeOnly ? '?active=true' : ''}`;
        const response = await apiFetch(url);
        if (!response.ok) throw new Error('Sınav oturumları alınamadı.');
        return response.json();
    },
    /** Koda göre oturum. Öğrenciye cevap anahtarı gönderilmez. */
    getEduAnalizExamSession: async (code: string): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/exam-sessions/${encodeURIComponent(code)}`);
        if (response.status === 404) return null;
        if (!response.ok) throw new Error('Sınav bilgisi alınamadı.');
        return response.json();
    },
    /** Öğrenci: sınava katılır. */
    joinEduAnalizExamSession: async (code: string, name: string): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/exam-sessions/${encodeURIComponent(code)}/join`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(hataMesaji(data, 'Sınava katılınamadı.'));
        return data;
    },
    /** Öğrenci: sınavı teslim eder. Puan sunucuda hesaplanır. */
    submitEduAnalizExamSession: async (code: string, payload: { name: string; answers: any; tabWarnings?: number }): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/exam-sessions/${encodeURIComponent(code)}/submit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(hataMesaji(data, 'Sınav teslim edilemedi.'));
        return data;
    },
    /** Öğretmen: oturum kaydını siler (teslim edilmiş cevaplar kalır). */
    deleteEduAnalizExamSession: async (code: string): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/exam-sessions/${encodeURIComponent(code)}`, {
            method: 'DELETE',
        });
        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(hataMesaji(data, 'Oturum silinemedi.'));
        }
        return response.json();
    },
    /** Öğretmen: oturumu kapatır. */
    endEduAnalizExamSession: async (code: string): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/exam-sessions/${encodeURIComponent(code)}/end`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
        });
        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            throw new Error(hataMesaji(data, 'Sınav kapatılamadı.'));
        }
        return response.json();
    },

    getEduAnalizHomeworks: async (createdBy?: string): Promise<any[]> => {
        const url = createdBy ? `${API_URL}/api/eduanaliz/homeworks?createdBy=${createdBy}` : `${API_URL}/api/eduanaliz/homeworks`;
        const response = await apiFetch(url);
        if (!response.ok) throw new Error('Failed to fetch EduAnaliz homeworks');
        return response.json();
    },
    saveEduAnalizHomework: async (hw: any): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/homeworks`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(hw),
        });
        if (!response.ok) throw new Error('Failed to save EduAnaliz homework');
        return response.json();
    },
    deleteEduAnalizHomework: async (id: string): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/homeworks/${id}`, { method: 'DELETE' });
        if (!res.ok) throw new Error('Failed to delete homework');
        return res.json();
    },

    getEduAnalizStudents: async (createdBy?: string): Promise<any[]> => {
        const url = createdBy ? `${API_URL}/api/eduanaliz/students?createdBy=${createdBy}` : `${API_URL}/api/eduanaliz/students`;
        const response = await apiFetch(url);
        if (!response.ok) throw new Error('Failed to fetch EduAnaliz students');
        return response.json();
    },
    saveEduAnalizStudent: async (student: any): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/students`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(student),
        });
        if (!response.ok) throw new Error('Failed to save EduAnaliz student');
        return response.json();
    },
    deleteEduAnalizStudent: async (id: string): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/students/${id}`, { method: 'DELETE' });
        if (!res.ok) throw new Error('Failed to delete student');
        return res.json();
    },

    // Deneme yönetimi
    denemeYukle: async (metin: string, tarih?: string, isim?: string): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme/yukle`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ metin, tarih, isim }),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.hata || 'Yükleme başarısız.');
        }
        return res.json();
    },
    denemeYuklePdf: async (formData: FormData): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme/yukle-pdf`, {
            method: 'POST',
            body: formData,
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.hata || 'PDF yükleme başarısız.');
        }
        return res.json();
    },
    getDenemeListesi: async (filtre?: { eslesmemis?: boolean }): Promise<any[]> => {
        const params = new URLSearchParams();
        if (filtre?.eslesmemis) params.set('eslesmemis', '1');
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme?${params}`);
        if (!res.ok) throw new Error('Liste alınamadı.');
        return res.json();
    },
    denemeEslestir: async (yayineviId: string, eslesmeler: ({ hamAd: string; studentId: string } | { kayitId: string; studentId: string })[]): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme/eslestir`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ yayineviId, eslesmeler }),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.hata || 'Eşleştirme başarısız.');
        }
        return res.json();
    },
    // Koçun elle verdiği konu eşleştirme kararlarını kaydeder. Karar kalıcıdır:
    // aynı yayınevinden gelen sonraki denemelerde kendiliğinden uygulanır ve
    // mevcut kayıtlara geriye dönük işlenir.
    denemeKonuSozluk: async (
        yayineviId: string,
        kararlar: { sinavTuru: string; ders: string; hamAd: string; kanonikAnahtar: string | null }[],
    ): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme/konu-sozluk`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ yayineviId, kararlar }),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.hata || 'Konu eşleştirmesi kaydedilemedi.');
        }
        return res.json();
    },
    // ── Deneme Yönetimi (yönetici paneli) ────────────────────────────────────
    denemeAdminOzet: async (): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme-admin/ozet`);
        if (!res.ok) throw new Error('Özet alınamadı.');
        return res.json();
    },
    denemeAdminKonuHavuzu: async (params: {
        durum?: string; yayineviId?: string; sinavTuru?: string; q?: string;
    } = {}): Promise<any> => {
        const qs = new URLSearchParams(
            Object.entries(params).filter(([, v]) => v) as [string, string][],
        ).toString();
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme-admin/konu-havuzu?${qs}`);
        if (!res.ok) throw new Error('Konu havuzu alınamadı.');
        return res.json();
    },
    denemeAdminKonuKarar: async (
        kararlar: { yayineviId: string; sinavTuru: string; ders: string; hamAd: string; kanonikAnahtar: string | null }[],
    ): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme-admin/konu-sozluk`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ kararlar }),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.hata || 'Karar kaydedilemedi.');
        }
        return res.json();
    },
    // Ders adı eşleştirme. Konu eşleştirmesiyle aynı akış: karar sistem
    // geneli yazılır ve o yayınevinin geçmiş kayıtlarına geriye dönük işlenir.
    denemeAdminDersHavuzu: async (params: {
        durum?: string; yayineviId?: string; sinavTuru?: string; q?: string; limit?: number;
    } = {}): Promise<any> => {
        const q = new URLSearchParams(
            Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
                .map(([k, v]) => [k, String(v)]),
        ).toString();
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme-admin/ders-havuzu?${q}`);
        if (!res.ok) throw new Error('Ders havuzu alınamadı.');
        return res.json();
    },
    denemeAdminDersKarar: async (
        kararlar: { yayineviId: string; sinavTuru: string; hamAd: string; kanonikAd: string | null }[],
    ): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme-admin/ders-sozluk`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ kararlar }),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.hata || 'Ders kararı kaydedilemedi.');
        }
        return res.json();
    },
    // ── WhatsApp ────────────────────────────────────────────────────────────
    // Erişim jetonu hiçbir uçtan dönmüyor; sunucu alan bazında ayıklıyor.
    whatsappDurum: async (): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/whatsapp/durum`);
        if (!res.ok) throw new Error('WhatsApp durumu alınamadı.');
        return res.json();
    },
    whatsappBaglan: async (govde: { kod: string; wabaId?: string; phoneNumberId?: string }): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/whatsapp/baglan`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(govde),
        });
        const veri = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(veri.message || 'WhatsApp bağlantısı kurulamadı.');
        return veri;
    },
    whatsappBaglantiKaldir: async (): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/whatsapp/baglanti`, { method: 'DELETE' });
        if (!res.ok) throw new Error('Bağlantı kaldırılamadı.');
        return res.json();
    },
    whatsappSablonDurumYenile: async (): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/whatsapp/sablon-durum-yenile`, { method: 'POST' });
        const veri = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(veri.message || 'Şablon durumları yenilenemedi.');
        return veri;
    },
    whatsappSablonKaydet: async (kod: string, govde: { metin?: string; aktif?: boolean }): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/whatsapp/sablon/${encodeURIComponent(kod)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(govde),
        });
        const veri = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(veri.message || 'Şablon kaydedilemedi.');
        return veri;
    },
    whatsappGidenMesajlar: async (params: { studentId?: string; durum?: string; limit?: number } = {}): Promise<any[]> => {
        const q = new URLSearchParams(
            Object.entries(params).filter(([, v]) => v !== undefined && v !== '')
                .map(([k, v]) => [k, String(v)]),
        ).toString();
        const res = await apiFetch(`${API_URL}/api/whatsapp/giden${q ? '?' + q : ''}`);
        if (!res.ok) throw new Error('Giden mesajlar alınamadı.');
        return res.json();
    },
    whatsappMesajIptal: async (id: string): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/whatsapp/giden/${encodeURIComponent(id)}/iptal`, { method: 'POST' });
        const veri = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(veri.message || 'Mesaj iptal edilemedi.');
        return veri;
    },

    denemeAdminYayinevleri: async (): Promise<any[]> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme-admin/yayinevleri`);
        if (!res.ok) throw new Error('Yayınevleri alınamadı.');
        return res.json();
    },
    denemeAdminKurallar: async (): Promise<any[]> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme-admin/kurallar`);
        if (!res.ok) throw new Error('Kurallar alınamadı.');
        return res.json();
    },
    denemeAdminKuralKaydet: async (kural: any): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme-admin/kurallar`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(kural),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.hata || 'Kural kaydedilemedi.');
        }
        return res.json();
    },
    denemeAdminKuralSil: async (yayineviId: string): Promise<any> => {
        const res = await apiFetch(
            `${API_URL}/api/eduanaliz/deneme-admin/kurallar/${encodeURIComponent(yayineviId)}`,
            { method: 'DELETE' },
        );
        if (!res.ok) throw new Error('Kural silinemedi.');
        return res.json();
    },
    // Kuralı kaydetmeden gerçek metin üzerinde çalıştırır.
    denemeAdminKuralDene: async (metin: string, kural: any): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme-admin/kural-dene`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ metin, kural }),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.hata || 'Deneme çalıştırılamadı.');
        }
        return res.json();
    },
    denemeAdminTalepler: async (durum?: string): Promise<any[]> => {
        const qs = durum ? `?durum=${encodeURIComponent(durum)}` : '';
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme-admin/talepler${qs}`);
        if (!res.ok) throw new Error('Talepler alınamadı.');
        return res.json();
    },
    denemeAdminTalep: async (id: string): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme-admin/talep/${id}`);
        if (!res.ok) throw new Error('Talep alınamadı.');
        return res.json();
    },
    denemeAdminTalepDurum: async (id: string, durum: string, yoneticiNotu?: string): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme-admin/talep/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ durum, yoneticiNotu }),
        });
        if (!res.ok) throw new Error('Durum güncellenemedi.');
        return res.json();
    },
    // Koç gönderir: tanınmayan deneme biçimini yöneticiye iletir.
    denemeYayineviTalebi: async (govde: {
        yayineviAdi: string; sinavTuru?: string; not?: string; hamMetin: string;
    }): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme-admin/talep`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(govde),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.hata || 'Talep gönderilemedi.');
        }
        return res.json();
    },
    // Koçun yüklediği tüm deneme kayıtları — toplu kütüphane görünümü için.
    getTumDenemeler: async (): Promise<any[]> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme`);
        if (!res.ok) throw new Error('Deneme listesi alınamadı.');
        return res.json();
    },
    denemeSil: async (id: string): Promise<void> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme/${id}`, { method: 'DELETE' });
        if (!res.ok) throw new Error('Silinemedi.');
    },
    denemeGrupSil: async (denemeAnahtari: string): Promise<{ silinen: number }> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme/sinav`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ denemeAnahtari }),
        });
        if (!res.ok) throw new Error((await res.json()).hata || 'Silinemedi.');
        return res.json();
    },
    getDenemeGecmisi: async (studentId: string): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme/ogrenci/${studentId}`);
        if (!res.ok) throw new Error('Geçmiş alınamadı.');
        return res.json();
    },
    getDenemeKonuAnalizi: async (studentId: string): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme/ogrenci/${studentId}/konu-analizi`);
        if (!res.ok) throw new Error('Konu analizi alınamadı.');
        return res.json();
    },
    getKocuKonuOzeti: async (): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/deneme/kocu/konu-ozeti`);
        if (!res.ok) throw new Error('Konu özeti alınamadı.');
        return res.json();
    },

    // Aksiyon Planı — verilen görevlerin kaydı. Sonuç ("tuttu/tutmadı")
    // sunucuda tutulmuyor, istemcide denemelerden türetiliyor.
    getAksiyonlar: async (studentId: string, sinavTuru?: string): Promise<any> => {
        const q = sinavTuru ? `?sinavTuru=${encodeURIComponent(sinavTuru)}` : '';
        const res = await apiFetch(`${API_URL}/api/eduanaliz/aksiyon/ogrenci/${studentId}${q}`);
        if (!res.ok) throw new Error('Aksiyonlar alınamadı.');
        return res.json();
    },
    aksiyonAta: async (studentId: string, aksiyonlar: any[]): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/aksiyon/ogrenci/${studentId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ aksiyonlar }),
        });
        if (!res.ok) throw new Error((await res.json()).hata || 'Aksiyon atanamadı.');
        return res.json();
    },
    aksiyonGuncelle: async (id: string, alanlar: any): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/aksiyon/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(alanlar),
        });
        if (!res.ok) throw new Error((await res.json()).hata || 'Güncellenemedi.');
        return res.json();
    },
    aksiyonSil: async (id: string): Promise<any> => {
        const res = await apiFetch(`${API_URL}/api/eduanaliz/aksiyon/${id}`, { method: 'DELETE' });
        if (!res.ok) throw new Error((await res.json()).hata || 'Silinemedi.');
        return res.json();
    },

    // EduAnaliz User Management
    getEduAnalizUsers: async (): Promise<any[]> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz-users`);
        if (!response.ok) throw new Error('Failed to fetch EA users');
        return response.json();
    },
    saveEduAnalizUser: async (user: any): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz-users`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(user),
        });
        if (!response.ok) {
            const errData = await response.json().catch(() => ({}));
            throw new Error(errData.error || 'Kullanıcı kaydedilemedi');
        }
        return response.json();
    },
    // E-posta doğrulama bağlantısındaki jetonu tüketir
    verifyEduAnalizEmail: async (token: string): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz-users/verify-email`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Doğrulama başarısız');
        return data;
    },

    // Doğrulama bağlantısını yeniden gönderir
    resendEduAnalizVerification: async (email: string): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz-users/resend-verification`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Bağlantı gönderilemedi');
        return data;
    },

    deleteEduAnalizUser: async (id: string): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz-users/${id}`, {
            method: 'DELETE',
        });
        if (!response.ok) throw new Error('Failed to delete EA user');
        return response.json();
    },
    // Support
    createSupportTicket: async (ticket: any): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/support`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(ticket),
        });
        if (!response.ok) throw new Error('Failed to create support ticket');
        return response.json();
    },
    // EduAnaliz Subscription & Invoices
    getEduAnalizSubscription: async (userId: string): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/subscription/${userId}`);
        if (!response.ok) throw new Error('Failed to fetch subscription');
        return response.json();
    },
    payEduAnalizSubscription: async (payload: any): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/subscription/pay`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error('Failed to make subscription payment');
        return response.json();
    },
    adminGetEduAnalizInvoices: async (): Promise<any[]> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/admin/invoices`);
        if (!response.ok) throw new Error('Failed to fetch admin invoices');
        return response.json();
    },
    adminApproveEduAnalizInvoice: async (invoiceId: string): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/admin/invoices/${invoiceId}/approve`, {
            method: 'POST',
        });
        if (!response.ok) throw new Error('Failed to approve invoice');
        return response.json();
    },
    adminRejectEduAnalizInvoice: async (invoiceId: string): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/admin/invoices/${invoiceId}/reject`, {
            method: 'POST',
        });
        if (!response.ok) throw new Error('Failed to reject invoice');
        return response.json();
    },
    getEduAnalizPackages: async (showAll?: boolean): Promise<any[]> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/packages${showAll ? '?all=true' : ''}`);
        if (!response.ok) throw new Error('Failed to fetch packages');
        return response.json();
    },
    saveEduAnalizPackage: async (pkg: any): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/admin/packages`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(pkg)
        });
        if (!response.ok) throw new Error('Failed to save package');
        return response.json();
    },
    deleteEduAnalizPackage: async (id: string): Promise<any> => {
        const response = await apiFetch(`${API_URL}/api/eduanaliz/admin/packages/${id}`, {
            method: 'DELETE',
        });
        if (!response.ok) throw new Error('Failed to delete package');
        return response.json();
    },
};
